import { useCallback, useEffect, useState } from 'react';
import { Banknote, CheckCircle2, Download, Loader2, Wallet } from 'lucide-react';
import { adminApi } from '../services/api';
import { annoncerSucces, Bandeau, EtatErreur, EtatSansDonnees, SqueletteTableau } from '../etats';
import EtatVide from './EtatVide';

/* ═══════════════════════════════════════════════════════════════════════
   DÉCAISSEMENT DES RELEVÉS D'INTERVENANTS

   La règle qui gouverne cet écran : **qui a validé ne peut pas payer.**
   Elle se vérifie sur CE relevé, pas sur les rôles — le point focal et
   le responsable financier sont souvent tous deux ADMIN, et une
   séparation posée au niveau du rôle ne protégerait rien.

   L'écran le dit AVANT le clic, en désactivant le bouton pour celui qui
   a validé. Le serveur refuse de toute façon ; mais découvrir une règle
   par un message d'erreur, c'est l'apprendre par l'échec.
   ═══════════════════════════════════════════════════════════════════════ */

const jour = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('fr-FR');
};
const francs = (v) => `${Number(v || 0).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} FCFA`;
const aujourdHui = () => new Date().toISOString().slice(0, 10);

function Paiement({ releve, moi, onPaye }) {
  const [ouvert, setOuvert] = useState(false);
  const [mode, setMode] = useState('virement');
  const [date, setDate] = useState(aujourdHui());
  const [reference, setReference] = useState('');
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState('');

  // Le nom du validateur plutôt que son identifiant : c'est ce que la
  // personne devant l'écran reconnaît.
  const jeSuisLeValidateur = releve.valide_par && releve.valide_par === moi;

  if (releve.statut === 'paye') {
    return (
      <span className="text-[12.5px] text-[var(--text-muted)]">
        Payé le {jour(releve.paye_le)}
        {releve.paye_par ? ` par ${releve.paye_par}` : ''}
        {releve.mode_paiement ? ` (${releve.mode_paiement})` : ''}
      </span>
    );
  }

  if (jeSuisLeValidateur) {
    return (
      <span className="text-[12.5px]" style={{ color: 'var(--attention)' }}>
        Vous avez validé ce relevé : un autre responsable doit le payer.
      </span>
    );
  }

  if (!ouvert) {
    return (
      <button type="button" className="act act-contour" onClick={() => setOuvert(true)}>
        <Banknote className="w-4 h-4" />
        Enregistrer le paiement
      </button>
    );
  }

  const payer = async () => {
    setEnCours(true); setErreur('');
    try {
      await adminApi.payerReleve(releve.id, { mode, date_paiement: date, reference });
      annoncerSucces(`Paiement du relevé ${releve.numero} enregistré.`);
      onPaye();
    } catch (e) {
      setErreur(e.message);
    } finally {
      setEnCours(false);
    }
  };

  return (
    <div className="text-left space-y-2 min-w-[240px]">
      {erreur && <div role="alert" className="encart encart-alerte">{erreur}</div>}
      <div className="flex gap-2 flex-wrap">
        <select value={mode} onChange={(e) => setMode(e.target.value)}
                className="champ" aria-label="Mode de paiement">
          <option value="virement">Virement bancaire</option>
          <option value="especes">Espèces</option>
        </select>
        {/* Ni future, ni antérieure à la validation : le serveur refuse
            les deux, et pour la même raison — un décaissement qu'aucun
            contrôle n'a précédé. */}
        <input type="date" value={date} max={aujourdHui()}
               onChange={(e) => setDate(e.target.value)}
               className="champ" aria-label="Date du paiement" />
      </div>
      <input value={reference} onChange={(e) => setReference(e.target.value)}
             placeholder="Référence (n° de virement, reçu…)" className="champ" />
      <div className="flex gap-2">
        <button type="button" className="act" disabled={enCours} aria-busy={enCours || undefined} onClick={payer}>
          {enCours ? <Loader2 className="w-4 h-4 animate-spin" />
            : <CheckCircle2 className="w-4 h-4" />}
          Confirmer {francs(releve.montant_net)}
        </button>
        <button type="button" className="act act-tertiaire" onClick={() => setOuvert(false)}>
          Annuler
        </button>
      </div>
    </div>
  );
}

export default function PaiementsIntervenants({ user }) {
  const [releves, setReleves] = useState(null);
  // L'objet erreur, pas son texte : son statut choisit l'écran.
  const [erreur, setErreur] = useState(null);

  const moi = [user?.first_name, user?.last_name].filter(Boolean).join(' ')
              || user?.username || '';

  const charger = useCallback(async () => {
    try {
      const data = await adminApi.relevesAPayer();
      setReleves(data.releves || []);
      setErreur(null);
    } catch (e) {
      setErreur(e);
    }
  }, []);

  useEffect(() => { charger(); }, [charger]);

  if (releves === null) {
    // Hors zone, le serveur répond 404 : « ce n'est pas votre écran »,
    // pas « c'est cassé ».
    if (erreur?.statut === 404) {
      return (
        <EtatSansDonnees cas="interdit"
                         aide="Le décaissement des relevés d'intervenants ne relève pas de votre accès." />
      );
    }
    return erreur
      ? <EtatErreur erreur={erreur} onReessayer={charger} />
      : <SqueletteTableau colonnes={6} />;
  }

  const aPayer = releves.filter((r) => r.statut === 'valide');

  return (
    <div className="space-y-4">
      {erreur && (
        <Bandeau ton="attention" action={<button type="button" className="act act-sm act-contour" onClick={charger}>Réessayer</button>}>
          L’actualisation a échoué : ces données sont peut-être anciennes.
        </Bandeau>
      )}

      {releves.length === 0 ? (
        <div className="rounded-carte bg-[var(--bg-card)] shadow-carte">
          <EtatVide
            icone={Wallet}
            titre="Aucun relevé à décaisser"
            aide="Un relevé n'arrive ici qu'une fois validé par le point focal."
          />
        </div>
      ) : (
        <div className="carte-tableau">
          <div className="p-5 pb-0">
            <h3 className="titre-carte">Relevés de prestation</h3>
            <p className="text-[12.5px] text-[var(--text-muted)] mt-1">
              {aPayer.length} en attente de décaissement sur {releves.length}.
            </p>
          </div>
          <div className="tableau-hote">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr>
                  <th scope="col">Référence</th>
                  <th scope="col">Prestataire</th>
                  <th scope="col">Mois</th>
                  <th scope="col" className="text-right">Net à payer</th>
                  <th scope="col">Validé par</th>
                  <th scope="col" className="text-right">Décaissement</th>
                </tr>
              </thead>
              <tbody>
                {releves.map((r) => (
                  <tr key={r.id}>
                    <th scope="row" className="font-normal">
                      <span className="text-[13.5px] font-medium text-[var(--text-bold)]">
                        {r.numero}
                      </span>
                      <a href={adminApi.urlPdfReleveSupervision(r.id)} target="_blank"
                         rel="noopener noreferrer"
                         className="block text-[11.5px] text-[var(--accent-texte)] hover:underline">
                        <Download className="w-3 h-3 inline mr-1" />voir le relevé
                      </a>
                    </th>
                    <td className="text-[13px]">{r.intervenant}</td>
                    <td className="text-[13px] text-[var(--text-muted)]">{jour(r.mois)}</td>
                    <td className="text-right text-[14px] font-semibold">
                      {francs(r.montant_net)}
                      <span className="block text-[11px] font-normal text-[var(--text-muted)]">
                        brut {francs(r.montant_brut)} − retenue {francs(r.montant_retenue)}
                      </span>
                    </td>
                    <td className="text-[12.5px]">
                      {r.valide_par || '—'}
                      <span className="block text-[11px] text-[var(--text-muted)]">
                        {jour(r.valide_le)}
                      </span>
                    </td>
                    <td className="text-right">
                      <Paiement releve={r} moi={moi} onPaye={charger} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <p className="text-[12px] text-[var(--text-muted)] leading-relaxed">
        <strong>Qui a validé ne peut pas payer.</strong> Deux personnes au moins
        doivent avoir vu chaque franc avant qu'il sorte — et cela se vérifie
        relevé par relevé, non par les rôles.
      </p>
    </div>
  );
}
